import { TriangleAlert, ChevronRight } from 'lucide-react';
import { Card, IconBox } from '@/ui/primitives';
import { cn } from '@/lib/utils';
import { resolveSkillDescription, resolveSkillIcon } from '../data';
import type { Skill } from '../schemas';

interface Props {
  skill: Skill;
  onSelect: () => void;
}

export function SkillCard({ skill, onSelect }: Props) {
  const Icon = resolveSkillIcon(skill.name);
  const desc = resolveSkillDescription(skill);
  const hasWarn = skill.status === 'warning';

  return (
    <Card
      hover
      className={cn(
        'flex items-start gap-3 cursor-pointer text-left',
        hasWarn && 'border-warning/40',
      )}
      onClick={onSelect}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
    >
      <IconBox tone={hasWarn ? 'warn' : 'accent'} size="lg">
        <Icon size={20} strokeWidth={1.5} />
      </IconBox>
      <div className="flex flex-col gap-1 flex-1 min-w-0">
        <span className="text-sm font-medium text-text truncate">{skill.name}</span>
        <span className="text-xs text-text-2 line-clamp-2">{desc}</span>
        {hasWarn && skill.warn && (
          <div className="flex items-center gap-1.5 text-[var(--warning-fg)] text-xs mt-1">
            <TriangleAlert size={12} strokeWidth={1.5} />
            <span className="truncate">{skill.warn}</span>
          </div>
        )}
      </div>
      <ChevronRight size={15} strokeWidth={1.5} className="text-text-muted shrink-0 mt-1" />
    </Card>
  );
}
